import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { getMySurveys } from '../../services/surveyService';

const Container = styled.div``;

const Title = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 20px 24px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  opacity: ${({ $expired }) => $expired ? 0.7 : 1};
  transition: box-shadow 0.15s;
  display: flex;
  flex-direction: column;
  ${({ $clickable }) => $clickable && `
    &:hover { box-shadow: 0 4px 12px rgba(0, 66, 66, 0.1); }
  `}
`;

const CardTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`;

const CardDescription = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 16px;
  flex: 1;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'pending' ? '#ca8a0415' :
    $status === 'completed' ? '#16a34a15' :
    $status === 'expired' ? '#ef444415' :
    $status === 'closed' ? '#64748b15' :
    '#9e9e9e15'};
  color: ${({ $status }) =>
    $status === 'pending' ? '#ca8a04' :
    $status === 'completed' ? '#16a34a' :
    $status === 'expired' ? '#ef4444' :
    $status === 'closed' ? '#64748b' :
    '#9e9e9e'};
`;

const DeadlineInfo = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #A0A8B4;
`;

const NoData = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #A0A8B4;
  text-align: center;
  padding: 60px 0;
`;

function getCardStatus(survey) {
  if (survey.participantStatus === 'completed') return 'completed';
  if (survey.status === 'closed') return 'closed';
  if (survey.deadline && new Date(survey.deadline) < new Date()) return 'expired';
  return 'pending';
}

function getDaysLeft(deadline) {
  if (!deadline) return 0;
  const diff = new Date(deadline) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export default function MySurveysPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getMySurveys()
      .then((res) => setSurveys(res.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleCardClick = (survey) => {
    const status = getCardStatus(survey);
    if (status === 'pending') history.push(`/surveys/fill/${survey.id}`);
    else if (status === 'completed') history.push(`/surveys/answers/${survey.id}`);
    else if (status === 'closed' && survey.participantStatus === 'completed') history.push(`/surveys/answers/${survey.id}`);
  };

  if (loading) return <Container><NoData>...</NoData></Container>;

  return (
    <Container>
      <Title>{t('survey.mySurveys')}</Title>

      {surveys.length === 0 ? (
        <NoData>{t('survey.noRecords')}</NoData>
      ) : (
        <Grid>
          {surveys.map((s) => {
            const cardStatus = getCardStatus(s);
            const isClickable = cardStatus === 'pending' || cardStatus === 'completed' || (cardStatus === 'closed' && s.participantStatus === 'completed');
            const daysLeft = getDaysLeft(s.deadline);
            const desc = s.description
              ? s.description.length > 120 ? s.description.slice(0, 120) + '...' : s.description
              : '';

            return (
              <Card
                key={s.id}
                $clickable={isClickable}
                $expired={cardStatus === 'expired'}
                onClick={() => isClickable && handleCardClick(s)}
              >
                <CardTitle>{s.title}</CardTitle>
                <CardDescription>{desc}</CardDescription>
                <CardFooter>
                  <StatusBadge $status={cardStatus}>
                    {cardStatus === 'pending' && t('survey.pending')}
                    {cardStatus === 'completed' && t('survey.completed')}
                    {cardStatus === 'expired' && t('survey.expired')}
                    {cardStatus === 'closed' && t('survey.closed')}
                  </StatusBadge>
                  <DeadlineInfo>
                    {cardStatus === 'pending' && t('survey.daysLeft', { count: daysLeft })}
                    {cardStatus === 'completed' && t('survey.submitted')}
                    {cardStatus === 'expired' && t('survey.expired')}
                    {cardStatus === 'closed' && t('survey.closed')}
                  </DeadlineInfo>
                </CardFooter>
              </Card>
            );
          })}
        </Grid>
      )}
    </Container>
  );
}
